import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatAmountInput,
  formatEuro,
  formatInteger,
  lifecycleLabels,
  listFilterCatalog,
  listFilterFields,
  listFilterValueError,
  parseAmountInput,
  type LifecycleKey,
  type ListFilter,
  type ListFilterField,
  type ListFilterOperator,
  type ListFilterValueKind,
} from '@brimo/shared';
import { Banner, Button, Checkbox, Select, TextField } from '../../../ds/components';
import { api, errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { useAssignees } from '../../contacts/api';
import { putListFilters, usePreviewCount, type ListDetail } from '../api';
import { Eyebrow } from '../ui';
import styles from './lists.module.css';

/**
 * Terminierung 05 — the filter editor of a smart view: the conditions as sunken rows (all must hold), a
 * row opens its field/operator/value editor, „Bedingung hinzufügen", and below the live count
 * („Treffer … davon N jetzt wählbar", `POST /dialer/lists/preview-count` over the draft).
 */

const LIFECYCLE_KEYS = Object.keys(lifecycleLabels) as LifecycleKey[];

function specOf(field: ListFilterField, operator: ListFilterOperator) {
  return listFilterCatalog[field].operators.find((op) => op.key === operator) ?? null;
}

interface Names {
  users: Map<string, string>;
  properties: Map<string, string>;
}

/** The value as the row shows it („Wiedervorlage", „30 Tage", „20.000 €", „J. Weiler, P. Göstl"). */
function valueText(kind: ListFilterValueKind, value: string | null, names: Names): string {
  if (value === null || value === '') return '';
  const parts = value.split(',').map((part) => part.trim());
  switch (kind) {
    case 'lifecycle_keys':
      return parts.map((key) => lifecycleLabels[key as LifecycleKey] ?? key).join(', ');
    case 'user_ids':
      return parts.map((id) => names.users.get(id) ?? '…').join(', ');
    case 'property_id':
      return names.properties.get(value) ?? '…';
    case 'days':
      return value === '1' ? '1 Tag' : `${value} Tage`;
    case 'cents':
      return formatEuro(Number(value));
    case 'count':
    case 'text':
      return value;
    case 'none':
      return '';
  }
}

function useProperties(enabled: boolean) {
  return useQuery({
    queryKey: [...queryRoots.properties, 'dialer-filter'],
    queryFn: () => api<{ items: Array<{ id: string; name: string }> }>('/properties?limit=100'),
    enabled,
    staleTime: 60_000,
  });
}

function sameFilters(a: ListFilter[], b: ListFilter[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function FilterCard({ list }: { list: ListDetail }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ListFilter[]>(list.filters);
  const [editing, setEditing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const users = useAssignees(true);
  const needsProperties = draft.some((filter) => filter.field === 'property') || editing !== null;
  const properties = useProperties(needsProperties);

  useEffect(() => {
    setDraft(list.filters);
    setEditing(null);
    setAdding(false);
  }, [list.filters]);

  const valid = draft.every((filter) => {
    const spec = specOf(filter.field, filter.operator);
    return spec !== null && listFilterValueError(spec.value, filter.value) === null;
  });
  const count = usePreviewCount(draft, valid);
  const dirty = !sameFilters(draft, list.filters);
  const hits = dirty ? count.data : { total: list.total, dialable: list.dialable };

  const names = useMemo<Names>(
    () => ({
      users: new Map((users.data ?? []).map((user) => [user.id, user.name])),
      properties: new Map((properties.data?.items ?? []).map((item) => [item.id, item.name])),
    }),
    [users.data, properties.data],
  );

  async function save() {
    setPending(true);
    setError(null);
    try {
      await putListFilters(list.id, draft);
      await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className={styles.sideCard} aria-label="Filter">
      <div className={styles.sideHead}>
        <Eyebrow>Filter</Eyebrow>
        <span className={styles.sideHeadNote}>alle Bedingungen müssen zutreffen</span>
      </div>
      <div className={styles.filters}>
        {draft.map((filter, index) =>
          editing === index ? (
            <FilterEditor
              key={index}
              filter={filter}
              users={users.data ?? []}
              properties={properties.data?.items ?? []}
              onApply={(next) => {
                setDraft((current) => current.map((item, position) => (position === index ? next : item)));
                setEditing(null);
                setAdding(false);
              }}
              onRemove={() => {
                setDraft((current) => current.filter((_, position) => position !== index));
                setEditing(null);
                setAdding(false);
              }}
              onCancel={() => {
                if (adding) setDraft((current) => current.filter((_, position) => position !== index));
                setEditing(null);
                setAdding(false);
              }}
            />
          ) : (
            <button
              key={index}
              type="button"
              className={`${styles.filterRow} ${styles.filterRowButton}`}
              disabled={!list.canEdit}
              onClick={() => setEditing(index)}
            >
              <span className={styles.filterField}>{listFilterCatalog[filter.field].label}</span>
              <span className={styles.filterOperator}>
                {specOf(filter.field, filter.operator)?.label ?? ''}
              </span>
              <span className={styles.filterValue}>
                {valueText(specOf(filter.field, filter.operator)?.value ?? 'none', filter.value, names)}
              </span>
            </button>
          ),
        )}
        {list.canEdit ? (
          <button
            type="button"
            className={styles.addCondition}
            disabled={editing !== null}
            onClick={() => {
              setDraft((current) => [...current, { field: 'lifecycle', operator: 'is', value: 'follow_up' }]);
              setEditing(draft.length);
              setAdding(true);
            }}
          >
            Bedingung hinzufügen
          </button>
        ) : null}
      </div>
      <div className={styles.hits} aria-live="polite">
        <span className={styles.hitsLabel}>Treffer</span>
        <span className={styles.hitsValue}>{hits ? formatInteger(hits.total) : '–'}</span>
        <span className={styles.hitsSub}>
          davon {hits ? formatInteger(hits.dialable) : '–'} jetzt wählbar
        </span>
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {dirty && list.canEdit ? (
        <div className={styles.filterSave}>
          <Button variant="secondary" size="small" onClick={() => setDraft(list.filters)}>
            Verwerfen
          </Button>
          <Button
            size="small"
            loading={pending}
            disabled={!valid || editing !== null}
            onClick={() => void save()}
          >
            Filter speichern
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function FilterEditor({
  filter,
  users,
  properties,
  onApply,
  onRemove,
  onCancel,
}: {
  filter: ListFilter;
  users: Array<{ id: string; name: string }>;
  properties: Array<{ id: string; name: string }>;
  onApply: (filter: ListFilter) => void;
  onRemove: () => void;
  onCancel: () => void;
}) {
  const [field, setField] = useState<ListFilterField>(filter.field);
  const [operator, setOperator] = useState<ListFilterOperator>(filter.operator);
  const initialKind = specOf(filter.field, filter.operator)?.value ?? 'none';
  const [value, setValue] = useState<string>(
    initialKind === 'cents' && filter.value ? formatAmountInput(Number(filter.value)) : (filter.value ?? ''),
  );
  const [error, setError] = useState<string | null>(null);
  const spec = specOf(field, operator) ?? listFilterCatalog[field].operators[0];
  const kind = spec?.value ?? 'none';
  const selected = value.split(',').filter(Boolean);

  function changeField(next: ListFilterField) {
    setField(next);
    const first = listFilterCatalog[next].operators[0];
    setOperator(first?.key ?? 'is');
    setValue('');
    setError(null);
  }

  function apply() {
    const stored =
      kind === 'none' ? null : kind === 'cents' ? String(parseAmountInput(value) ?? '') : value.trim();
    const problem = listFilterValueError(kind, stored);
    if (problem !== null) {
      setError(problem);
      return;
    }
    onApply({ field, operator: spec?.key ?? operator, value: stored });
  }

  function toggle(id: string, on: boolean) {
    const next = on ? [...selected, id] : selected.filter((item) => item !== id);
    setValue(next.join(','));
  }

  return (
    <div className={styles.filterEdit} role="group" aria-label="Bedingung bearbeiten">
      <div className={styles.filterEditRow}>
        <Select
          label="Feld"
          size="compact"
          value={field}
          options={listFilterFields.map((key) => ({ value: key, label: listFilterCatalog[key].label }))}
          onChange={(event) => changeField(event.target.value as ListFilterField)}
        />
        <Select
          label="Operator"
          size="compact"
          value={spec?.key ?? operator}
          options={listFilterCatalog[field].operators.map((op) => ({ value: op.key, label: op.label }))}
          onChange={(event) => setOperator(event.target.value as ListFilterOperator)}
        />
      </div>
      {kind === 'lifecycle_keys' ? (
        <fieldset className={styles.dialogFieldset}>
          <legend className={styles.dialogLabel}>Wert</legend>
          {LIFECYCLE_KEYS.map((key) => (
            <Checkbox
              key={key}
              label={lifecycleLabels[key]}
              checked={selected.includes(key)}
              onChange={(event) => toggle(key, event.target.checked)}
            />
          ))}
        </fieldset>
      ) : kind === 'user_ids' ? (
        <fieldset className={styles.dialogFieldset}>
          <legend className={styles.dialogLabel}>Wert</legend>
          {users.map((user) => (
            <Checkbox
              key={user.id}
              label={user.name}
              checked={selected.includes(user.id)}
              onChange={(event) => toggle(user.id, event.target.checked)}
            />
          ))}
        </fieldset>
      ) : kind === 'property_id' ? (
        <Select
          label="Wert"
          size="compact"
          placeholder="Bitte wählen"
          value={value}
          options={properties.map((item) => ({ value: item.id, label: item.name }))}
          onChange={(event) => setValue(event.target.value)}
        />
      ) : kind === 'none' ? null : (
        <TextField
          label={kind === 'days' ? 'Wert · Tage' : kind === 'cents' ? 'Wert · Euro' : 'Wert'}
          size="compact"
          inputMode={kind === 'text' ? 'text' : 'numeric'}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
      )}
      {error ? <span className={styles.dialogError}>{error}</span> : null}
      <div className={styles.filterEditActions}>
        <Button variant="ghost" size="small" onClick={onRemove}>
          Entfernen
        </Button>
        <span className={styles.end}>
          <Button variant="secondary" size="small" onClick={onCancel}>
            Abbrechen
          </Button>{' '}
          <Button size="small" onClick={apply}>
            Übernehmen
          </Button>
        </span>
      </div>
    </div>
  );
}
